import { Folder } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from './useFetchFolderContents';
import { GET_FOLDER_HIERARCHY_QUERY_KEY } from './useFolderHierarchy';
import { GET_FOLDER_QUERY_KEY } from './useGetFolder';

export const useRenameFolder = ({
  folder,
  onSuccess,
  onError,
}: {
  folder?: Folder;
  onSuccess?: (updatedFolder: Folder) => void;
  onError?: (error: string) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();

  const { mutate, isPending, error } = useMutation({
    mutationKey: ['renameFolder', folder?.id],
    mutationFn: (name: string) => api.folders.renameFolder(tenantId, folder!.id, { name }),
    onSuccess: data => {
      onSuccess?.(data.data.folder);
      queryClient.resetQueries({
        queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY],
        exact: false,
      });
      queryClient.removeQueries({
        queryKey: [GET_FOLDER_QUERY_KEY],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: [GET_FOLDER_HIERARCHY_QUERY_KEY],
        exact: false,
      });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      onError?.(error.response?.data.message ?? 'Failed to rename folder');
    },
  });

  return {
    renameFolder: mutate,
    isRenaming: isPending,
    error,
  };
};
